import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { PageHeader, Card } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { aed } from "@/lib/format";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import api from "@/lib/api";
import { useReportBranch } from "@/contexts/ReportBranchContext";
import { printBranchComparisonReport } from "@/lib/miscReportPrint";

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function BranchComparisonReport() {
  const { branchId, allBranches } = useReportBranch();
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [run, setRun] = useState(false);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["branch-comparison", fromDate, toDate],
    queryFn: async () => {
      const { data: res } = await api.get("/franchise/branch-comparison", {
        params: { date_from: fromDate, date_to: toDate },
      });
      return res;
    },
    enabled: run,
  });

  return (
    <ReportPageWrap>
      <PageHeader
        title="Branch Comparison"
        subtitle="AOV, delivery %, food cost %, and table turn by branch"
        actions={
          <ReportExportButtons
            disabled={!data}
            title="Branch Comparison"
            filename="branch-comparison"
            onPrint={() =>
              printBranchComparisonReport(
                { ...data, from_date: fromDate, to_date: toDate },
                { branchId, allBranches },
              )
            }
          />
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label>From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <Button
            onClick={() => {
              setRun(true);
              refetch();
            }}
            disabled={isFetching}
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}
          </Button>
        </div>
      </Card>

      {data && (
        <ReportTableCard title="Branch comparison">
          <table>
            <thead>
              <tr>
                <th className="text-left">Branch</th>
                <th className="text-left">City</th>
                <th className="text-right">Net sales</th>
                <th className="text-right">Round off</th>
                <th className="text-right">Bills</th>
                <th className="text-right">AOV</th>
                <th className="text-right">Delivery %</th>
                <th className="text-right">Food cost %</th>
                <th className="text-right">Table turn</th>
              </tr>
            </thead>
            <tbody>
              {(data.rows ?? []).map((row) => (
                <tr key={row.branch_id}>
                  <td className="font-medium">{row.branch_name}</td>
                  <td>{row.city || "—"}</td>
                  <td className="text-right font-mono">{aed(row.net_sales)}</td>
                  <td className="text-right font-mono">{aed(row.round_off)}</td>
                  <td className="text-right">{row.bill_count}</td>
                  <td className="text-right font-mono">{aed(row.avg_order_value)}</td>
                  <td className="text-right">{row.delivery_percent}%</td>
                  <td className="text-right">{row.food_cost_percent ?? "—"}%</td>
                  <td className="text-right">{row.table_turn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}
    </ReportPageWrap>
  );
}
